import React, { useEffect, useMemo, useState } from "react";
import { MaterialReactTable } from "material-react-table";
import type { MRT_ColumnDef } from "material-react-table";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Chip,
  Stack,
  Tabs,
  Tab,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axiosInstance from "../../Utils/axiosInstance";
import Accordion from "react-bootstrap/Accordion";

const PnlSummary = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [pnlType, setPnlType] = useState<string>("combined"); // daily/monthly/combined
  const [activeTab, setActiveTab] = useState<"company" | "counterparty">(
    "company"
  ); // default tab

  // API map for company
  const pnlApiMap: Record<string, string> = {
    combined: "/paper-app/combined-pnl/",
    daily: "/paper-app/combined-daily-pnl/",
    monthly: "/paper-app/combined-monthly-pnl/",
  };

  // API map for counterparty (customer_pnl=True)
  const counterpartyApiMap: Record<string, string> = {
    combined: "/paper-app/combined-pnl/?customer_pnl=True",
    daily: "/paper-app/combined-daily-pnl/?customer_pnl=True",
    monthly: "/paper-app/combined-monthly-pnl/?customer_pnl=True",
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const apiMap = activeTab === "company" ? pnlApiMap : counterpartyApiMap;
        const res = await axiosInstance.get(apiMap[pnlType]);
        setRows(res.data || []);
      } catch (err) {
        console.error(`Error fetching ${pnlType} PnL for ${activeTab}:`, err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [pnlType, activeTab]);

  const handlePnlTypeChange = (type: string) => {
    setPnlType(type);
  };

  const handleClearFilter = () => {
    setPnlType("combined");
  };

  const handleTabChange = (
    _: React.SyntheticEvent,
    newValue: "company" | "counterparty"
  ) => {
    setActiveTab(newValue);
    setPnlType("combined"); // reset filter when tab changes
  };

  const formatNumber = (value: any) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Styling constants
  const META_BG = "#e0e0e0";
  const FUTURES_BG = "transparent";
  const SWAPS_BG = "transparent";
  const TOTALS_BG = "transparent";
  const HEADER_BG = "#f0f2f5";
  const BORDER_COLOR = "#8a99a6";

  const renderCell = (value: any) => {
    const num = parseFloat(value);
    if (isNaN(num)) return value;
    return (
      <span style={{ color: num < 0 ? "red" : num > 0 ? "green" : "black" }}>
        {formatNumber(num)}
      </span>
    );
  };

  const getUniqueValues = (key: string) =>
    Array.from(new Set(rows.map((row) => row[key]))).filter(Boolean);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "Clearer",
        header: "Clearer",
        filterVariant: "select",
        filterSelectOptions: getUniqueValues("Clearer"),
        enableColumnFilter: true,
        muiTableBodyCellProps: {
          sx: {
            borderRight: `1px solid ${BORDER_COLOR}`,
            borderBottom: `1px solid ${BORDER_COLOR}`,
            backgroundColor: META_BG, // or FUTURES_BG, SWAPS_BG, TOTALS_BG
          },
        },

        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Book",
        header: "Book",
        filterVariant: "select",
        filterSelectOptions: getUniqueValues("Book"),
        enableColumnFilter: true,
        muiTableBodyCellProps: {
          sx: {
            borderRight: `1px solid ${BORDER_COLOR}`,
            borderBottom: `1px solid ${BORDER_COLOR}`,
            backgroundColor: META_BG, // or FUTURES_BG, SWAPS_BG, TOTALS_BG
          },
        },

        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },

      {
        accessorKey: "Strategy",
        header: "Strategy",
        filterVariant: "select",
        filterSelectOptions: getUniqueValues("Strategy"),
        enableColumnFilter: true,
        muiTableBodyCellProps: {
          sx: {
            borderRight: `1px solid ${BORDER_COLOR}`,
            borderBottom: `1px solid ${BORDER_COLOR}`,
            backgroundColor: META_BG, // or FUTURES_BG, SWAPS_BG, TOTALS_BG
          },
        },

        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },

      // --- Futures ---
      {
        accessorKey: "Futures Gross PnL",
        header: "Futures Gross PnL",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: FUTURES_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Exchange Clearance Fee",
        header: "Exchange Clearance Fee",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: FUTURES_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Cash Settle Fee",
        header: "Cash Settle Fee",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: FUTURES_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Futures Brokerage",
        header: "Futures Brokerage",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: FUTURES_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Futures Total Fees",
        header: "Futures Total Fees",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: FUTURES_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Futures PnL",
        header: "Futures PnL",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: FUTURES_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },

      // --- Swaps ---
      {
        accessorKey: "Swaps Gross PnL",
        header: "Swaps Gross PnL",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: SWAPS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Swaps Brokerage",
        header: "Swaps Brokerage",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: SWAPS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Swaps Total Fees",
        header: "Swaps Total Fees",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: SWAPS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Swaps PnL",
        header: "Swaps PnL",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: SWAPS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },

      // --- Totals ---
      {
        accessorKey: "Total Gross PnL",
        header: "Total Gross PnL",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: TOTALS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Total Brokerage",
        header: "Total Brokerage",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: TOTALS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Total Fees",
        header: "Total Fees",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: TOTALS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
      {
        accessorKey: "Net PnL",
        header: "Net PnL",
        Cell: ({ cell }) => renderCell(cell.getValue()),
        muiTableBodyCellProps: {
          align: "right",
          sx: { backgroundColor: TOTALS_BG },
        },
        muiFilterTextFieldProps: () => ({
          size: "small",
          sx: {
            fontSize: "0.65rem", // smaller font
            minWidth: "70px", // narrower
            "& .MuiInputBase-root": {
              height: "26px", // smaller height
              fontSize: "0.65rem", // smaller input font
            },
            "& .MuiSelect-select": {
              padding: "0px 4px", // minimal padding
              fontSize: "0.65rem",
              lineHeight: 1.2,
            },
            "& .MuiInputBase-input": {
              padding: "0px 4px",
              fontSize: "0.65rem",
            },
          },
          SelectProps: {
            MenuProps: {
              PaperProps: {
                sx: {
                  fontSize: "0.65rem", // dropdown item font smaller
                  maxHeight: 120, // shorter dropdown
                  "& .MuiMenuItem-root": {
                    fontSize: "0.65rem",
                    minHeight: "24px", // reduce row height
                    padding: "0px 6px",
                  },
                },
              },
            },
          },
        }),
      },
    ],
    [rows] // important: recalc filter options whenever rows change
  );

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="page-body">
      <div className="table-ad__wrapper typeCapitalize">
        {/* ✅ Accordion replacing the old Box heading */}
        <Accordion
          defaultActiveKey="0"
          className="table-ad__addInputs"
          style={{ width: "100%" }}
        >
          <Accordion.Item eventKey="0">
            <Accordion.Header
              className="accordion_button"
              style={{
                fontSize: "0.8rem",
                backgroundColor: "#d1edf1",
                padding: "6px 12px",
                borderRadius: "4px",
                fontWeight: 600,
              }}
            >
              P&L Summary
            </Accordion.Header>

            <Accordion.Body>
              {/* Tabs for Company / Counterparty */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 36,
                  "& .MuiTab-root": {
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "0.85rem",
                    px: 2,
                    minHeight: 36,
                    py: 0.5,
                    color: "#555",
                    borderRight: "1px solid #e0e0e0",
                    "&:last-of-type": { borderRight: "none" },
                  },
                  "& .Mui-selected": {
                    color: "#1976d2",
                    backgroundColor: "#ffffff",
                  },
                  "& .MuiTabs-indicator": {
                    height: "2px",
                  },
                }}
              >
                <Tab value="company" label={<span>Company P&L</span>} />
                <Tab
                  value="counterparty"
                  label={<span>Counterparty P&L</span>}
                />
              </Tabs>

              {/* Chips (Summary / Daily / Monthly) */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                mb={1}
                mt={2}
              >
                {["combined", "daily", "monthly"].map((type) => (
                  <Chip
                    key={type}
                    label={
                      type === "combined"
                        ? "Summary"
                        : type.charAt(0).toUpperCase() + type.slice(1)
                    }
                    size="small"
                    clickable
                    color={pnlType === type ? "primary" : "default"}
                    onClick={() => handlePnlTypeChange(type)}
                    deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                    onDelete={
                      pnlType === type && type !== "combined"
                        ? handleClearFilter
                        : undefined
                    }
                    sx={{
                      borderRadius: "4px",
                      fontWeight: pnlType === type ? 500 : 400,
                      backgroundColor: pnlType === type ? "#00bcd4" : "#e0e0e0",
                      color: pnlType === type ? "#fff" : "#000",
                      "& .MuiChip-deleteIcon": {
                        color: "#fff",
                        ml: 0.5,
                      },
                      // ✅ remove hover effect
                      "&:hover": {
                        backgroundColor:
                          pnlType === type ? "#00bcd4" : "#e0e0e0",
                      },
                    }}
                  />
                ))}
              </Stack>

              {/* Table Section */}
              <div style={{ marginTop: 0 }}>
                <div className="table-ad__wrapperCard">
                  <MaterialReactTable
                    columns={columns}
                    data={rows}
                    enableColumnFilters
                    enableSorting
                    enablePagination={false}
                    enablePinning
                    initialState={{
                      showColumnFilters: true,
                      density: "compact",
                      pagination: { pageSize: 25, pageIndex: 0 },
                      columnPinning: { left: ["Clearer", "Book", "Strategy"] },
                    }}
                    columnFilterDisplayMode="subheader"
                    layoutMode="grid"
                    muiTableContainerProps={{
                      sx: {
                        maxHeight: "70vh",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                        backgroundColor: "#ffffff",
                      },
                    }}
                    muiTableHeadRowProps={{
                      sx: {
                        position: "sticky",
                        top: 0,
                        background: "white",
                        zIndex: 5,
                        "& .MuiTableCell-root": {
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          color: "#2c3e50",
                          borderRight: "1px solid #dcdcdc",
                        },
                      },
                    }}
                    muiTableBodyCellProps={{
                      sx: {
                        fontSize: "0.7rem",
                        padding: "4px 8px",
                        borderBottom: "1px solid #f1f1f1",
                        "&:hover": {
                          backgroundColor: "#fafafa",
                        },
                      },
                    }}
                    muiBottomToolbarProps={{
                      sx: {
                        backgroundColor: "#fff",
                        padding: "8px",
                      },
                      className: "bottom-toolbar-wrapper__new",
                    }}
                    muiTopToolbarProps={{
                      sx: {
                        backgroundColor: "#fff",
                        padding: "8px",
                        "& .MuiBox-root.css-wsew38": {
                          backgroundColor: "#fff",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        },
                      },
                      className: "top-toolbar-wrapper__new",
                    }}
                    muiPaginationProps={{
                      shape: "rounded",
                      size: "small",
                      sx: {
                        fontSize: "0.7rem",
                        "& .MuiButtonBase-root": {
                          fontSize: "0.7rem",
                          minWidth: "28px",
                          height: "28px",
                        },
                      },
                    }}
                    muiToolbarAlertBannerProps={{
                      sx: {
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        fontSize: "0.75rem",
                        borderRadius: 1,
                      },
                    }}
                  />
                </div>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

export default PnlSummary;
